import { useEffect, useId, useMemo, useState } from "react";
import {
  BLOCK_KINDS, blockKindLabel, defaultBlockLabel, diaryGrid, eveningLine, HOUR_CHOICES, LABEL_MAX, 
  nextWeekGrid, plannedLine, protectedHours, protectFormProblem, nextWeekBlocksFromView, slotName, spentSoFar,
  type BlockKind, type GridCell, type Half, type NextWeekBlock, type NextWeekCell, type Weekday,
} from "../diary";
import { formatDate, formatHours } from "../format";
import type { ObservedView, PlayerAction } from "../types";
import { Modal } from "./Dialogs";

interface Slot { week: number; day: Weekday; half: Half; next: boolean }

/**
 * The diary (21 §9–10, 19 §4.3): this week as space, what is booked, what is protected,
 * and where the overflow goes. Planning is offered, never required.
 */
export function DiaryPanel({ view, runId, busy, onAct }: {
  view: ObservedView; runId: string; busy: boolean; onAct: (a: PlayerAction) => Promise<ObservedView | undefined>;
}) {
  const diary = view.diary;
  const grid = useMemo(() => diaryGrid(diary), [diary]);
  // Next week comes from the engine; nothing is kept in the browser.
  const nextBlocks = useMemo(() => nextWeekBlocksFromView(diary), [diary]);
  const [slot, setSlot] = useState<Slot | null>(null);
  const [status, setStatus] = useState("");
  const next = useMemo(() => nextWeekGrid(diary, nextBlocks), [diary, nextBlocks]);

  const first = diary.days[0]?.date;
  const last = diary.days[diary.days.length - 1]?.date;
  const spent = spentSoFar(diary);
  const prot = protectedHours(diary);

  const protect = async (s: Slot, f: { label: string; kind: BlockKind; hours: number; personId: string; recurring: boolean }) => {
    const recurring = f.kind === "one_to_one" && f.recurring;
    const action: PlayerAction = {
      type: "protect", week: s.week, day: s.day, half: s.half, hours: f.hours, label: f.label.trim(), kind: f.kind,
      ...(f.kind === "one_to_one" && f.personId ? { personId: f.personId } : {}), ...(recurring ? { recurring: true } : {}),
    };
    const res = await onAct(action);
    if (!res) return false;
    setStatus(`Protected ${slotName(s.day, s.half, s.next)}.`);
    return true;
  };

  const unprotect = async (s: Slot) => {
    const res = await onAct({ type: "unprotect", week: s.week, day: s.day, half: s.half });
    if (res) setStatus(`Released ${slotName(s.day, s.half, s.next)}.`);
  };

  return (
    <section className="panel diary" aria-labelledby="diary-title">
      <h2 id="diary-title" className="panel-title">Diary · week {diary.week}</h2>
      {first && last && <p className="muted">{`Mon ${formatDate(first)} – Fri ${formatDate(last)}`}</p>}
      <p className="diary__note">
        Protected time keeps ordinary interruptions out. Someone with something urgent may still ask to come in.
        A standing one-to-one helps you read that person.
      </p>
      <p className="visually-hidden" role="status" aria-live="polite">{status}</p>

      {/* The grid keeps its shape on narrow screens and scrolls sideways; the region can take focus so it can be scrolled by keyboard. */}
      <div className="table-scroll" tabIndex={0} role="region" aria-labelledby="diary-this-caption">
        <table className="diary-grid">
          <caption id="diary-this-caption" className="visually-hidden">This week, by morning and afternoon</caption>
          <thead>
            <tr>
              <td />
              {grid.columns.map((c) => (
                <th key={c.day} scope="col">{c.label} <span className="diary-grid__date">{c.date ? formatDate(c.date) : ""}</span></th>
              ))}
            </tr>
          </thead>
          <tbody>
            {grid.rows.map((r) => (
              <tr key={r.half}>
                <th scope="row">{r.label}</th>
                {r.cells.map((c) => (
                  <ThisWeekCell key={c.day} c={c} busy={busy}
                    onProtect={() => setSlot({ week: diary.week, day: c.day, half: c.half, next: false })}
                    onUnprotect={() => void unprotect({ week: diary.week, day: c.day, half: c.half, next: false })} />
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="evening-band" role="note">
        <span className="evening-band__label">Evenings and weekends</span>
        <span className="evening-band__value">{eveningLine(diary.eveningHours)}</span>
      </div>

      <dl className="diary-totals">
        <div><dt>Planned this week</dt><dd>{plannedLine(diary.plannedHours, diary.officeHours)}</dd></div>
        <div><dt>Logged so far this week</dt><dd className="num">{formatHours(spent)}</dd></div>
        <div><dt>Protected</dt><dd className="num">{prot > 0 ? formatHours(prot) : "Nothing yet"}</dd></div>
      </dl>

      <h3 className="diary__sub">Next week</h3>
      {diary.nextWeek.length === 0
        ? <p className="muted">Nothing is in the diary for next week yet.</p>
        : (
          <ul className="diary-next">
            {diary.nextWeek.map((a, i) => <li key={i}><strong>{slotName(a.day, a.half as Half)}:</strong> {a.label}</li>)}
          </ul>
        )}
      <div className="table-scroll" tabIndex={0} role="region" aria-labelledby="diary-next-caption">
        <table className="diary-grid diary-grid--next">
          <caption id="diary-next-caption" className="visually-hidden">Next week, by morning and afternoon</caption>
          <thead>
            <tr><td />{grid.columns.map((c) => <th key={c.day} scope="col">{c.label}</th>)}</tr>
          </thead>
          <tbody>
            {next.map((r) => (
              <tr key={r.half}>
                <th scope="row">{r.label}</th>
                {r.cells.map((c) => (
                  <NextWeekCellView key={c.day} c={c} busy={busy}
                    onProtect={() => setSlot({ week: diary.week + 1, day: c.day, half: c.half, next: true })}
                    onUnprotect={() => void unprotect({ week: diary.week + 1, day: c.day, half: c.half, next: true })} />
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="muted small">You can plan this week and next. Bank holidays next week are not marked until the week starts.</p>

      {slot && (
        <ProtectDialog slot={slot} view={view} busy={busy} onClose={() => setSlot(null)}
          onSubmit={async (f) => { if (await protect(slot, f)) setSlot(null); }} />
      )}
    </section>
  );
}

function BlockView({ label, kind, hours, raided }: { label: string; kind: string; hours: number; raided?: boolean }) {
  return (
    <div className={`block block--protected${raided ? " block--broken" : ""}`}>
      <span className="block__lock" aria-hidden="true">🔒</span>
      <span className="block__label">{label}</span>
      <span className="block__meta">Protected · {blockKindLabel(kind)} · {formatHours(hours)}</span>
      {raided && <span className="block__broken">Broken: an interruption came in</span>}
    </div>
  );
}

function ThisWeekCell({ c, busy, onProtect, onUnprotect }: { c: GridCell; busy: boolean; onProtect: () => void; onUnprotect: () => void }) {
  const where = slotName(c.day, c.half);
  const showSpent = (c.status === "past" || c.status === "now") && c.cell.spent > 0;
  return (
    <td className={`diary-cell diary-cell--${c.status}`} aria-current={c.status === "now" ? "time" : undefined}>
      {c.status === "now" && <span className="diary-cell__now">Now</span>}
      {c.status === "past" && <span className="visually-hidden">Past. </span>}
      {c.status === "holiday" ? (
        <span className="diary-cell__holiday">Bank holiday</span>
      ) : (
        <>
          {c.cell.appointments.length > 0 && (
            <ul className="diary-cell__appts">
              {c.cell.appointments.map((a, i) => <li key={i}>{a.label} <span className="muted">· {formatHours(a.hours)}</span></li>)}
            </ul>
          )}
          {c.cell.protected && <BlockView {...c.cell.protected} />}
          {showSpent && <p className="diary-cell__spent">{formatHours(c.cell.spent)} spent</p>}
          {c.canProtect && (
            <button type="button" className="btn btn--small" disabled={busy} onClick={onProtect} aria-label={`Protect time on ${where}`}>Protect…</button>
          )}
          {c.canUnprotect && (
            <button type="button" className="btn btn--small btn--quiet" disabled={busy} onClick={onUnprotect} aria-label={`Remove the protected block on ${where}`}>Remove</button>
          )}
        </>
      )}
    </td>
  );
}

function NextWeekCellView({ c, busy, onProtect, onUnprotect }: { c: NextWeekCell; busy: boolean; onProtect: () => void; onUnprotect: () => void }) {
  const where = slotName(c.day, c.half, true);
  return (
    <td className="diary-cell diary-cell--future">
      {c.appointments.length > 0 && <ul className="diary-cell__appts">{c.appointments.map((a, i) => <li key={i}>{a}</li>)}</ul>}
      {c.block && <BlockView {...c.block} />}
      {c.block?.fromThisWeek && <p className="diary-cell__spent">Repeats from this week</p>}
      {!c.block && (
        <button type="button" className="btn btn--small" disabled={busy} onClick={onProtect} aria-label={`Protect time on ${where}`}>Protect…</button>
      )}
      {c.block && !c.block.fromThisWeek && (
        <button type="button" className="btn btn--small btn--quiet" disabled={busy} onClick={onUnprotect} aria-label={`Remove the protected block on ${where}`}>Remove</button>
      )}
    </td>
  );
}

/** Which field a protect-form problem is about (see protectFormProblem). */
function problemField(problem: string): "label" | "who" | "hours" {
  if (problem.startsWith("Choose who")) return "who";
  if (problem.includes("half an hour")) return "hours";
  return "label";
}

function ProtectDialog({ slot, view, busy, onClose, onSubmit }: {
  slot: Slot; view: ObservedView; busy: boolean; onClose: () => void;
  onSubmit: (f: { label: string; kind: BlockKind; hours: number; personId: string; recurring: boolean }) => Promise<void>;
}) {
  const ids = useId();
  const [kind, setKind] = useState<BlockKind>("design");
  const [personId, setPersonId] = useState("");
  const [hours, setHours] = useState(2);
  const [recurring, setRecurring] = useState(true);
  const [label, setLabel] = useState(defaultBlockLabel("design"));
  const [touched, setTouched] = useState(false);
  const [tried, setTried] = useState(false);
  // People who are in, other than you (the player has no profile of themselves).
  const people = view.people.filter((p) => p.availability === "in" && view.profiles[p.id]);
  const personName = people.find((p) => p.id === personId)?.name ?? null;
  // Keep the suggested label in step until the player types their own.
  useEffect(() => { if (!touched) setLabel(defaultBlockLabel(kind, personName)); }, [kind, personName, touched]);
  const form = { label, kind, hours, personId, recurring };
  const problem = protectFormProblem(form);
  const shown = tried ? problem : null;
  // The message is tied to the field it is about (WCAG 3.3.1), and focus goes to that field on a failed submit.
  const field = shown ? problemField(shown) : null;
  const errId = `${ids}-error`;
  const invalid = (f: "label" | "who" | "hours") => field === f ? { "aria-invalid": true as const, "aria-describedby": errId } : {};

  return (
    <Modal title={`Protect ${slotName(slot.day, slot.half, slot.next)}`} open onClose={onClose}>
      <form className="form protect-form" noValidate onSubmit={(e) => {
        e.preventDefault();
        setTried(true);
        if (!problem) { void onSubmit(form); return; }
        requestAnimationFrame(() => document.getElementById(`${ids}-${problemField(problem)}`)?.focus());
      }}>
        <fieldset>
          <legend>What is it for?</legend>
          {BLOCK_KINDS.map((k) => (
            <label key={k.id} className="check">
              <input type="radio" name={`${ids}-kind`} checked={kind === k.id} onChange={() => setKind(k.id)} />
              <span><strong>{k.label}</strong> <span className="muted">{k.hint}</span></span>
            </label>
          ))}
        </fieldset>
        {kind === "one_to_one" && (
          <>
            <label htmlFor={`${ids}-who`}>With</label>
            <select id={`${ids}-who`} value={personId} onChange={(e) => setPersonId(e.target.value)} {...invalid("who")}>
              <option value="">Choose someone…</option>
              {people.map((p) => <option key={p.id} value={p.id}>{p.name}, {p.role}</option>)}
            </select>
            <label className="check">
              <input type="checkbox" checked={recurring} onChange={(e) => setRecurring(e.target.checked)} />
              <span>Repeat weekly</span>
            </label>
          </>
        )}
        <label htmlFor={`${ids}-label`}>Label</label>
        <input id={`${ids}-label`} type="text" value={label} maxLength={LABEL_MAX}
          onChange={(e) => { setTouched(true); setLabel(e.target.value); }} {...invalid("label")} />
        <label htmlFor={`${ids}-hours`}>Hours</label>
        <select id={`${ids}-hours`} value={hours} onChange={(e) => setHours(Number(e.target.value))} {...invalid("hours")}>
          {HOUR_CHOICES.map((h) => <option key={h} value={h}>{formatHours(h)}</option>)}
        </select>
        <p className="muted small">
          Protected time keeps ordinary interruptions out. Someone with something urgent may still ask to come in,
          and you can choose whether to let them.
        </p>
        <div role="alert">{shown && <p className="form__error" id={errId}>{shown}</p>}</div>
        <div className="row-actions">
          <button type="submit" className="btn btn--primary" disabled={busy}>Protect this time</button>
          <button type="button" className="btn btn--quiet" onClick={onClose}>Cancel</button>
        </div>
      </form>
    </Modal>
  );
}
